import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Compass,
  Settings2,
  Settings,
  LogOut,
  Menu,
  X,
  LifeBuoy,
  LayoutGrid,
  CalendarClock,
  Calendar as CalIcon,
  CalendarCheck,
  Boxes,
  Users,
  FileText,
  BarChart3,
  Banknote,
  Globe, CreditCard, HelpCircle, Mail, UserRound, Sparkles, ClipboardCheck, Smartphone } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import TaskCenter from "./TaskCenter";

const navItems = [
  { label: "Tableau de bord", href: "/vendor/pilotage?mode=junago", icon: Compass },
  { label: "Junago Copilote", href: "/vendor/pilotage?mode=copilot", icon: Sparkles },
  { label: "Mon site internet", href: "/vendor/vendre", icon: Globe },
  { label: "Paramétrages", href: "/vendor/parametrages", icon: Settings2 },
]; // eslint-disable-line no-unused-vars

// Barre de navigation inférieure mobile (4 raccourcis + Tous les modules)
const BOTTOM_NAV = [
  { key: "today", label: "Aujourd'hui", icon: CalendarClock },
  { key: "calendar", label: "Calendrier", icon: CalIcon },
  { key: "bookings", label: "Réservations", icon: CalendarCheck },
  { key: "vehicles", label: "Flotte", icon: Boxes },
];

export default function VendorLayout({ children, dark = false, mode = null, mainPadBottom = true }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [modulesOpen, setModulesOpen] = React.useState(false);
  const [pagePublished, setPagePublished] = React.useState(null);
  const [pageSlug, setPageSlug] = React.useState(null);
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [tasksOpen, setTasksOpen] = React.useState(false);
  const [unseenTasks, setUnseenTasks] = React.useState(0);
  const [taskUpdateAvail, setTaskUpdateAvail] = React.useState(typeof window !== "undefined" && !!window.__junagoUpdateReady);
  React.useEffect(() => {
    const on = () => setTaskUpdateAvail(true);
    window.addEventListener("junago-update-available", on);
    return () => window.removeEventListener("junago-update-available", on);
  }, []);
  const taskBadge = unseenTasks + (taskUpdateAvail ? 1 : 0);
  const [installAvail, setInstallAvail] = React.useState(false);
  const isStandalone = typeof window !== "undefined" && ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true);
  React.useEffect(() => {
    if (typeof window !== "undefined" && window.__junagoInstall) setInstallAvail(true);
    const onAvail = () => setInstallAvail(true);
    const onDone = () => setInstallAvail(false);
    window.addEventListener("junago-install-available", onAvail);
    window.addEventListener("junago-install-done", onDone);
    return () => {
      window.removeEventListener("junago-install-available", onAvail);
      window.removeEventListener("junago-install-done", onDone);
    };
  }, []);
  const doInstall = async () => {
    const e = typeof window !== "undefined" ? window.__junagoInstall : null;
    if (!e) return;
    setMobileOpen(false);
    e.prompt();
    try { await e.userChoice; } catch (_) {}
    window.__junagoInstall = null;
    setInstallAvail(false);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const curParams = new URLSearchParams(location.search);
  const onPilotage = location.pathname.startsWith("/vendor/pilotage");
  const curTab = onPilotage ? (curParams.get("tab") || (typeof window !== "undefined" ? localStorage.getItem("jn_last_tab") : null) || "calendar") : null;
  const isCopilot = mode === "copilot";
  const gotoTab = (t) => { setModulesOpen(false); setMobileOpen(false); navigate(`/vendor/pilotage?tab=${t}&mode=junago`); };

  const handleTaskCounts = React.useCallback((c) => setUnseenTasks(c.unseen), []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const goSite = () => {
    window.open("/", "_blank", "noopener,noreferrer");
  };

  const goHelp = () => {
    window.open("/assistance", "_blank", "noopener,noreferrer");
  };

  React.useEffect(() => {
    api.get("/vendor/settings").then(({ data }) => {
      setPagePublished(!!data.shop?.published);
      setPageSlug(data.shop?.slug || null);
    }).catch(() => {});
  }, []);

  // Escape ferme les menus · Ctrl+K ouvre le Copilot
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setAccountOpen(false);
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        navigate("/vendor/pilotage?mode=copilot");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <div className={`min-h-screen overflow-x-hidden ${dark ? "bg-[#0B0B16]" : "bg-slate-50"}`}>
      {/* ───── Boutons flottants haut-droit — identiques partout ───── */}
      <div className="hidden lg:flex items-center gap-2 fixed top-3 right-3 z-50" data-testid="vendor-topright-cluster">
        {/* Sélecteur d'interface — les 3 univers */}
        <div
          className={`flex items-center h-10 rounded-full border p-1 transition-colors ${dark ? "border-white/15 bg-[#12121F]" : "border-slate-200 bg-white"}`}
          data-testid="mode-switcher"
        >
          <button
            onClick={() => navigate("/vendor/pilotage?mode=junago")}
            title="Tableau de bord"
            className={`h-8 px-3.5 rounded-full text-[11px] font-semibold inline-flex items-center justify-center transition-colors ${mode === "junago" ? "bg-slate-900 text-white" : dark ? "text-slate-400 hover:text-slate-200 hover:bg-white/5" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}
            data-testid="mode-manual-btn"
          >
            Tableau de bord
          </button>
          <button
            onClick={() => navigate("/vendor/pilotage?mode=copilot")}
            title="Junago Copilote — IA (Ctrl+K)"
            className={`h-8 px-3.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${mode === "copilot" ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white" : dark ? "text-violet-300 hover:text-violet-200 hover:bg-white/5" : "text-violet-500 hover:text-violet-600 hover:bg-violet-50"}`}
            data-testid="mode-copilot-btn"
          >
            <Sparkles className="h-3 w-3" /> Copilote
          </button>
          <button
            onClick={() => navigate("/vendor/vendre")}
            title="Mon site internet de réservation"
            className={`h-8 px-3.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center gap-1.5 transition-colors ${
              location.pathname.startsWith("/vendor/vendre")
                ? "bg-slate-900 text-white"
                : pagePublished
                  ? dark ? "text-emerald-300 hover:text-emerald-200 hover:bg-white/5" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  : dark ? "text-slate-400 hover:text-slate-200 hover:bg-white/5" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
            }`}
            data-testid="page-status-btn"
          >
            <span className="relative flex h-2 w-2">
              {pagePublished && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${pagePublished ? "bg-emerald-400" : "bg-slate-300"}`} />
            </span>
            Boutique
          </button>
        </div>
        {/* Gestionnaire de tâches */}
        <button
          onClick={() => { setTasksOpen((o) => !o); setAccountOpen(false); }}
          data-testid="taskcenter-btn"
          title="Gestionnaire de tâches"
          aria-label="Gestionnaire de tâches"
          className={`relative h-10 w-10 rounded-full border flex items-center justify-center transition-colors ${
            tasksOpen
              ? "bg-slate-900 border-slate-900 text-white"
              : dark
                ? "border-white/15 bg-[#12121F] text-slate-200 hover:bg-white/10"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          <ClipboardCheck className="h-[18px] w-[18px]" />
          {taskBadge > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="taskcenter-badge">
              {taskBadge > 99 ? "99+" : taskBadge}
            </span>
          )}
        </button>
        {/* Compte */}
        <div className="relative">
            <button
              onClick={() => setAccountOpen((o) => !o)}
              data-testid="account-menu-btn"
              className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${dark ? "bg-white/10 text-white hover:bg-white/20 border border-white/15" : "bg-slate-900 text-white hover:bg-slate-800"}`}
              aria-label="Mon compte"
              title="Mon compte"
            >
              <UserRound className="h-4 w-4" strokeWidth={2.2} />
            </button>
          {accountOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setAccountOpen(false)} />
              <div className={`absolute right-0 top-12 z-50 w-64 rounded-2xl border p-2 shadow-2xl ${dark ? "bg-[#12121F] border-white/10" : "bg-white border-slate-200 shadow-slate-300/40"}`} data-testid="account-menu" style={{ animation: "jnAccIn .18s ease-out both" }}>
                <div className={`px-3 py-2.5 mb-1 rounded-xl ${dark ? "bg-white/5" : "bg-slate-50"}`}>
                  <div className={`text-xs font-bold truncate ${dark ? "text-white" : "text-slate-900"}`}>{user?.name}</div>
                  <div className={`text-[10px] truncate ${dark ? "text-slate-400" : "text-slate-500"}`}>{user?.company || user?.email}</div>
                </div>
                {[
                  { key: "voir-site", label: pagePublished ? "Voir mon site" : "Ma page (hors ligne)", icon: Globe, onClick: () => { setAccountOpen(false); if (pagePublished && pageSlug) window.open(`/p/${pageSlug}`, "_blank"); else navigate("/vendor/vendre"); } },
                  { key: "parametrages", label: "Paramétrages", icon: Settings, onClick: () => { setAccountOpen(false); navigate("/vendor/parametrages"); } },
                  { key: "paiements", label: "Mes paiements", icon: CreditCard, soon: true },
                  { key: "faq", label: "FAQ", icon: HelpCircle, onClick: () => { setAccountOpen(false); goHelp(); } },
                  { key: "assistance", label: "Centre d'assistance", icon: LifeBuoy, onClick: () => { setAccountOpen(false); goHelp(); } },
                  { key: "contact", label: "Contactez-nous", icon: Mail, soon: true },
                ].map((it) => (
                  <button
                    key={it.key}
                    onClick={it.soon ? undefined : it.onClick}
                    disabled={it.soon}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${it.soon ? (dark ? "text-slate-600 cursor-not-allowed" : "text-slate-300 cursor-not-allowed") : (dark ? "text-slate-200 hover:bg-white/5" : "text-slate-700 hover:bg-slate-50")}`}
                    title={it.soon ? "Bientôt disponible" : undefined}
                    data-testid={`account-menu-${it.key}`}
                  >
                    <it.icon className="h-4 w-4 shrink-0" /> {it.label}
                    {it.soon && <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold ${dark ? "bg-white/10 text-slate-400" : "bg-slate-100 text-slate-400"}`}>Bientôt</span>}
                  </button>
                ))}
                <div className={`my-1 h-px ${dark ? "bg-white/10" : "bg-slate-100"}`} />
                <button onClick={handleLogout} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${dark ? "text-red-400 hover:bg-red-500/10" : "text-red-600 hover:bg-red-50"}`} data-testid="account-menu-logout">
                  <LogOut className="h-4 w-4 shrink-0" /> Se déconnecter
                </button>
              </div>
            </>
          )}
          </div>
      </div>
      <style>{`@keyframes jnAccIn{from{opacity:0;transform:translateY(-6px) scale(.97)}to{opacity:1;transform:none}}@keyframes jnSheetUp{from{opacity:0;transform:translateY(100%)}to{opacity:1;transform:none}}@keyframes jnSheetRight{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:none}}`}</style>
      <TaskCenter open={tasksOpen} onClose={() => setTasksOpen(false)} dark={dark} onCounts={handleTaskCounts} />

      {/* ───── Mobile header — une seule ligne : Logo | Tableau de bord | Copilote | Tâches | Menu ───── */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-40 backdrop-blur-md border-b h-14 pl-3 pr-2 flex items-center gap-1 ${dark ? "bg-[#0B0B16]/95 border-white/10" : "bg-white/95 border-slate-200"}`} style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <button onClick={() => navigate("/vendor/pilotage?mode=junago")} className="flex items-center shrink-0 mr-auto" data-testid="vendor-mobile-logo" aria-label="Junago — Accueil">
          <span className="h-8 w-8 rounded-xl bg-slate-900 text-white flex items-center justify-center font-heading font-bold text-sm">J</span>
          <span className={`hidden min-[380px]:inline ml-2 font-heading font-semibold text-sm ${dark ? "text-white" : "text-slate-900"}`}>Junago<span className="text-violet-600">.</span></span>
        </button>
        {/* Tableau de bord */}
        <button
          onClick={() => navigate("/vendor/pilotage?mode=junago")}
          data-testid="mobile-header-dashboard"
          aria-label="Tableau de bord"
          title="Tableau de bord"
          className={`h-9 w-9 rounded-xl flex items-center justify-center transition-colors shrink-0 ${
            onPilotage && !isCopilot ? "bg-violet-50 text-violet-700" : dark ? "text-slate-300 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"
          }`}
        >
          <Compass className="h-[18px] w-[18px]" strokeWidth={onPilotage && !isCopilot ? 2.3 : 2} />
        </button>
        {/* Copilote */}
        <button
          onClick={() => navigate("/vendor/pilotage?mode=copilot")}
          data-testid="mobile-header-copilot"
          aria-label="Junago Copilote"
          title="Junago Copilote"
          className={`h-9 w-9 rounded-xl flex items-center justify-center transition-colors shrink-0 ${
            isCopilot ? "bg-violet-600 text-white" : dark ? "text-violet-300 hover:bg-white/10" : "text-violet-600 hover:bg-violet-50"
          }`}
        >
          <Sparkles className="h-[18px] w-[18px]" strokeWidth={isCopilot ? 2.3 : 2} />
        </button>
        {/* Gestionnaire de tâches */}
        <button
          onClick={() => { setTasksOpen((o) => !o); setMobileOpen(false); setModulesOpen(false); }}
          data-testid="taskcenter-btn-mobile"
          aria-label="Gestionnaire de tâches"
          title="Gestionnaire de tâches"
          className={`relative h-9 w-9 rounded-xl flex items-center justify-center transition-colors shrink-0 ${
            tasksOpen ? "bg-violet-50 text-violet-700" : dark ? "text-slate-300 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"
          }`}
        >
          <ClipboardCheck className="h-[18px] w-[18px]" />
          {taskBadge > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
              {taskBadge > 99 ? "99+" : taskBadge}
            </span>
          )}
        </button>
        {/* Menu */}
        <button
          onClick={() => { setMobileOpen((o) => !o); setModulesOpen(false); }}
          data-testid="vendor-mobile-menu"
          aria-label="Menu"
          title="Menu"
          className={`h-9 w-9 rounded-xl flex items-center justify-center transition-colors shrink-0 ${
            mobileOpen ? "bg-violet-50 text-violet-700" : dark ? "text-slate-300 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"
          }`}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* ───── Menu « général » (compte + fonctions transverses) ───── */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 top-14 z-30 bg-slate-900/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="lg:hidden fixed top-14 right-0 z-30 w-[86%] max-w-xs h-[calc(100vh-56px)] bg-white shadow-2xl px-4 py-4 space-y-1.5 overflow-y-auto" data-testid="vendor-mobile-menu-panel" style={{ animation: "jnSheetRight .26s cubic-bezier(.2,.8,.2,1) both" }}>
            <div className="px-3 py-2.5 mb-2 rounded-xl bg-slate-50">
              <div className="text-xs font-bold truncate text-slate-900">{user?.name}</div>
              <div className="text-[10px] truncate text-slate-500">{user?.company || user?.email}</div>
            </div>
            <button onClick={() => { setMobileOpen(false); if (pagePublished && pageSlug) window.open(`/p/${pageSlug}`, "_blank"); else navigate("/vendor/vendre"); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="mobile-menu-site">
              <Globe className="h-4 w-4" /> {pagePublished ? "Voir mon site" : "Mon site internet"}
            </button>
            <button onClick={() => { setMobileOpen(false); navigate("/vendor/parametrages"); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="mobile-menu-settings">
              <Settings className="h-4 w-4" /> Paramètres
            </button>
            <button onClick={() => { setMobileOpen(false); goHelp(); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="goto-help-btn-mobile">
              <LifeBuoy className="h-4 w-4" /> Centre d'assistance
            </button>
            {installAvail && !isStandalone && (
              <button onClick={doInstall} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100" data-testid="pwa-install-btn">
                <Smartphone className="h-4 w-4" /> Installer l'application
              </button>
            )}
            <div className="my-1.5 h-px bg-slate-100" />
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50" data-testid="mobile-menu-logout">
              <LogOut className="h-4 w-4" /> Se déconnecter
            </button>
          </div>
        </>
      )}

      {/* ───── Feuille « Tous les modules » ───── */}
      {modulesOpen && (
        <>
          <div className="lg:hidden fixed inset-0 z-[55] bg-slate-900/40 backdrop-blur-sm" onClick={() => setModulesOpen(false)} data-testid="modules-sheet-backdrop" />
          <div className="lg:hidden fixed left-0 right-0 bottom-0 z-[56] bg-white rounded-t-3xl shadow-2xl px-4 pt-3 pb-[calc(16px+env(safe-area-inset-bottom))] max-h-[82vh] overflow-y-auto" data-testid="modules-sheet" style={{ animation: "jnSheetUp .24s cubic-bezier(.2,.8,.2,1) both" }}>
            <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-slate-200" />
            <div className="flex items-center justify-between px-1 mb-2">
              <h3 className="font-heading text-base font-semibold text-slate-900">Tous les modules</h3>
              <button onClick={() => setModulesOpen(false)} className="h-8 w-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100" data-testid="modules-sheet-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "Clients", icon: Users, onClick: () => gotoTab("clients") },
                { label: "Devis", icon: FileText, onClick: () => gotoTab("quotes") },
                { label: "Statistiques", icon: BarChart3, onClick: () => gotoTab("stats") },
                { label: "Revenus", icon: Banknote, onClick: () => gotoTab("revenue") },
              ].map((m) => (
                <button
                  key={m.label}
                  onClick={m.onClick}
                  data-testid={`modules-sheet-item-${m.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="flex flex-col items-center justify-center gap-1.5 py-3.5 rounded-2xl bg-slate-50 hover:bg-slate-100 active:scale-95 transition-all"
                >
                  <m.icon className="h-5 w-5 text-slate-700" />
                  <span className="text-[11px] font-medium text-slate-700 text-center leading-tight">{m.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ───── Barre de navigation inférieure mobile ───── */}
      {!isCopilot && (
        <nav className={`lg:hidden fixed left-0 right-0 bottom-0 z-40 border-t flex items-stretch ${dark ? "bg-[#0B0B16]/95 border-white/10" : "bg-white/95 border-slate-200"} backdrop-blur-md`} data-testid="mobile-bottom-nav" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          {BOTTOM_NAV.map((it) => {
            const active = onPilotage && curTab === it.key;
            return (
              <button
                key={it.key}
                onClick={() => gotoTab(it.key)}
                data-testid={`bottom-nav-${it.key}`}
                className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 h-14 transition-colors ${
                  active ? "text-violet-600" : dark ? "text-slate-400" : "text-slate-500"
                }`}
              >
                <it.icon className={`h-5 w-5 ${active ? "" : "opacity-90"}`} strokeWidth={active ? 2.4 : 2} />
                <span className="text-[10px] font-medium leading-none truncate max-w-full px-0.5">{it.label}</span>
              </button>
            );
          })}
          <button
            onClick={() => { setModulesOpen(true); setMobileOpen(false); }}
            data-testid="bottom-nav-modules"
            className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 h-14 transition-colors ${
              modulesOpen ? "text-violet-600" : dark ? "text-slate-400" : "text-slate-500"
            }`}
          >
            <LayoutGrid className="h-5 w-5" strokeWidth={modulesOpen ? 2.4 : 2} />
            <span className="text-[10px] font-medium leading-none truncate max-w-full px-0.5">Modules</span>
          </button>
        </nav>
      )}

      {/* ───── Main content ───── */}
      <main className={`min-w-0 pt-14 lg:pt-0 ${mainPadBottom && !isCopilot ? "pb-[calc(56px+env(safe-area-inset-bottom))] lg:pb-0" : ""}`}>{children}</main>
    </div>
  );
}
